import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {AUTH_TOKEN} from 'constants/AuthConstant';
import AuthService from 'services/AuthService';
import {LOCATION_KEY} from "../../configs/AppConfig";
import service from "../../auth/FetchInterceptor";

export const initialState = {
	loading: false,
	message: '',
	showMessage: false,
	redirect: '',
	token: localStorage.getItem(AUTH_TOKEN) || null,
	currentUser: null,
	notifications: [],
	twoFactorRequired: false,
	twoFactorEmail: null,
}

export const signIn = createAsyncThunk('auth/login',async (data, { rejectWithValue }) => {
	const { email, password } = data
	try {
		const response = await AuthService.login({email, password})

		if (response.token) {
			localStorage.setItem(AUTH_TOKEN, response.token);
		}
		return response;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const verifyTwoFactorCode = createAsyncThunk('auth/verifyTwoFactorCode', async (data, { rejectWithValue }) => {
	const { email, code } = data
	try {
		const response = await AuthService.verifyTwoFactorCode({email, code})

		const token = response.token;
		localStorage.setItem(AUTH_TOKEN, token);
		return response;
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const resendTwoFactorCode = createAsyncThunk('auth/resendTwoFactorCode', async (email, { rejectWithValue }) => {
	try {
		return await AuthService.resendTwoFactorCode({email})
	} catch (err) {
		return rejectWithValue(err.response?.data?.message || 'Error')
	}
})

export const markNotificationAsRead = createAsyncThunk(
	'auth/markNotificationAsRead',
	async (notificationId, { rejectWithValue }) => {
		try {
			await service({
				method: 'POST',
				url: `notifications/${notificationId}/mark-as-read`
			});
			return notificationId;
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error');
		}
	}
);

export const fetchCurrentUser = createAsyncThunk(
	'current-user',
	async (_, { rejectWithValue }) => {
		try {
			return await service({
				method: 'GET',
				url: 'current-user'
			});
		} catch (err) {
			return rejectWithValue(err.response?.data?.message || 'Error')
		}
	}
)


export const signOut = createAsyncThunk('auth/logout',async () => {
	localStorage.removeItem(LOCATION_KEY);
	localStorage.removeItem(AUTH_TOKEN);

	return null
})


export const authSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		setCurrentUser: (state, action) => {
			state.currentUser = action.payload
		},
		setNotifications: (state, action) => {
			state.notifications = action.payload
		},
		setUnreadNotificationsCount: (state, action) => {
			if (state.currentUser) state.currentUser.unread_notifications_count = action.payload
		},
		authenticated: (state, action) => {
			state.loading = false
			state.redirect = '/'
			state.token = action.payload
		},
		showAuthMessage: (state, action) => {
			state.message = action.payload
			state.showMessage = true
			state.loading = false
		},
		hideAuthMessage: (state) => {
			state.message = ''
			state.showMessage = false
		},
		signOutSuccess: (state) => {
			state.loading = false
			state.token = null
			state.redirect = '/'
		},
		showLoading: (state) => {
			state.loading = true
		},
		signInSuccess: (state, action) => {
			state.loading = false
			state.token = action.payload
		},
		resetTwoFactor: (state) => {
			state.twoFactorRequired = false
			state.twoFactorEmail = null
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(signIn.pending, (state) => {
				state.loading = true
			})
			.addCase(signIn.fulfilled, (state, action) => {
				state.loading = false
				if (action.payload.token) {
					state.redirect = '/'
					state.token = action.payload.token
					return
				}
				state.twoFactorRequired = true
				state.twoFactorEmail = action.payload.email
			})
			.addCase(signIn.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(verifyTwoFactorCode.pending, (state) => {
				state.loading = true
			})
			.addCase(verifyTwoFactorCode.fulfilled, (state, action) => {
				state.loading = false
				state.redirect = '/'
				state.token = action.payload.token
				state.twoFactorRequired = false
				state.twoFactorEmail = null
			})
			.addCase(verifyTwoFactorCode.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(resendTwoFactorCode.pending, (state) => {
				state.loading = true
			})
			.addCase(resendTwoFactorCode.fulfilled, (state) => {
				state.loading = false
			})
			.addCase(resendTwoFactorCode.rejected, (state, action) => {
				state.message = action.payload
				state.showMessage = true
				state.loading = false
			})
			.addCase(signOut.fulfilled, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(signOut.rejected, (state) => {
				state.loading = false
				state.token = null
				state.redirect = '/'
			})
			.addCase(fetchCurrentUser.pending, (state) => {
				state.loading = true
			})
			.addCase(fetchCurrentUser.fulfilled, (state, action) => {
				state.loading = false
				state.currentUser = action.payload
			})
			.addCase(fetchCurrentUser.rejected, (state, action) => {
				state.loading = false
			})

			.addCase(markNotificationAsRead.fulfilled, (state, action) => {
				state.notifications = state.notifications.map(notification =>
					notification.id === action.payload
						? { ...notification, read_at: new Date().toISOString() }
						: notification
				);

				if (state.currentUser && state.currentUser.unread_notifications_count > 0) {
					state.currentUser.unread_notifications_count -= 1;
				}
			})
	},
})

export const {
	authenticated,
	showAuthMessage,
	hideAuthMessage,
	signOutSuccess,
	showLoading,
	signInSuccess,
	setCurrentUser,
	setNotifications,
	setUnreadNotificationsCount,
	resetTwoFactor
} = authSlice.actions

export default authSlice.reducer